import {useEffect, useState} from "react";
import * as React from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  IconButton,
  Flex,
  Spacer,
  Box,
  Select, Input
} from "@chakra-ui/react";

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TriangleDownIcon,
  TriangleUpIcon
} from "@chakra-ui/icons";

import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table";

export function DataTable({columns, defaultSorting, dataFetch, refreshDependencies, ...props}) {
  const [sorting, setSorting] = useState(defaultSorting || []);
  const [search, setSearch] = useState('')
  const [customFilters, setCustomFilters] = useState([]);
  const [columnFilters, setColumnFilters] = useState()

  const [paginationData, setPaginationData] = useState({
    totalRows: 0,
    totalPages: 0,
  });
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [data, setData] = useState([])


  const refreshData = async (exportData = false) => {
    let pageSize = pagination.pageSize;
    let pageIndex = pagination.pageIndex;
    if (exportData) {
      pageSize = null;
      pageIndex = 0;
    }

    const filters = columnFilters ? columnFilters.map(filter => ({
      key: filter.id,
      operator: 'startsWith',
      value: filter.value
    })) : []

    // sorting arranca en [] (array vacío), que es "truthy" — sin el chequeo de length
    // esta rama de fallback nunca corría y siempre se mandaba order: [] al backend.
    const sorts = sorting && sorting.length ? sorting.map(sort => ([sort.id, sort.desc ? 'DESC' : 'ASC'])) : [['id', 'desc']]

    const data = await dataFetch(
      pageSize,
      pageIndex + 1,
      sorts,
      search,
      [...customFilters, ...filters]
    );

    if (!exportData) {
      setPaginationData({
        totalRows: data.totalRows,
        totalPages: data.totalPages,
      });
      setData(data.data);
    }
    return data;
  };

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    rowCount: paginationData.totalRows,
    pageCount: paginationData.totalPages,
    manualPagination: true,
    autoResetPageIndex: false,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    state: {
      sorting,
      pagination,
      columnFilters
    }
  });

  useEffect(() => {
    if (dataFetch) refreshData();
  }, [search, pagination, sorting, columnFilters, refreshDependencies]);

  useEffect(() => {
    table.resetPagination()
  }, [columnFilters]);

  return (
    <div>

      <Table variant='striped'  {...props}>
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                const meta = header.column.columnDef.meta;
                return (
                  <Th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    isNumeric={meta?.isNumeric}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}

                    <chakra.span pl="4">
                      {header.column.getIsSorted() ? (
                        header.column.getIsSorted() === "desc" ? (
                          <TriangleDownIcon aria-label="sorted descending"/>
                        ) : (
                          <TriangleUpIcon aria-label="sorted ascending"/>
                        )
                      ) : null}
                      {header.column.getCanFilter() ? (
                        <div>
                          <Filter column={header.column} table={table}/>
                        </div>
                      ) : null}
                    </chakra.span>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => {
            const style = {
              color: 'gray',
              fontStyle: 'italic'
            }
            return (
              <Tr key={row.id} style={row.original.deletedAt && style}>
                {row.getVisibleCells().map((cell) => {
                  // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                  const meta = cell.column.columnDef.meta;
                  return (
                    <Td key={cell.id} isNumeric={meta?.isNumeric}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  );
                })}
              </Tr>
            )
          })}
        </Tbody>
      </Table>
      <Flex marginTop={4}>
        <Box>
          Pagina {' '}
          <strong>
            {table.getState().pagination.pageIndex + 1} de{' '}
            {table.getPageCount()}
          </strong>
          {' '}{paginationData.totalRows} registros
        </Box>
        <Spacer/>
        <Box>

          <IconButton
            size={'sm'}
            isRound={true}
            variant='outline'
            aria-label='Done'
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            icon={<ArrowLeftIcon/>}
          />
          <IconButton
            marginX={'2'}
            size={'sm'}
            isRound={true}
            variant='outline'
            aria-label='Done'
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            icon={<ChevronLeftIcon/>}
          />

          <IconButton
            size={'sm'}
            marginX={'2'}
            isRound={true}
            variant='outline'
            aria-label='Done'
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            icon={<ChevronRightIcon/>}
          />
          <IconButton
            size={'sm'}
            isRound={true}
            variant='outline'
            aria-label='Done'
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
            icon={<ArrowRightIcon/>}
          />
        </Box>
        <Spacer/>

        <Box>
          <Select
            size={'sm'}
            value={table.getState().pagination.pageSize}
            onChange={e => {
              table.setPageSize(Number(e.target.value))
            }}
          >
            {[10, 20, 30, 40, 50, 100].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Mostrar {pageSize}
              </option>
            ))}
          </Select>
        </Box>
      </Flex>
    </div>
  );
}

function Filter({column, table}) {
  const columnFilterValue = column.getFilterValue()

  return (
    <Input
      size={'xs'}
      borderRadius={6}
      onChange={e => column.setFilterValue(e.target.value)}
      onClick={e => e.stopPropagation()}
      placeholder={`Filtrar`}
      type="text"
      value={(columnFilterValue ?? '')}
    />
  )
}